/**
 * Ziehungs-Modell (docs/02 v1.1 + docs/01 Fokus-System): unabhaengige gewichtete
 * Ziehungen, KEIN geteilter Pool. Einstufig nach der Spec-Formel:
 *   Gewicht(Eintrag) = Basis(100) x Stage-Odds(Runde) x Fokus-Modifikator
 * S3 v1.2: Stage-Odds-Matrix (loest A9 ab); Seltenheits-Fokus ist ein RELATIVER
 * Multiplikator (nie negativ); Gewicht 0 = AUSGESCHLOSSEN (kein Epsilon-Leck);
 * Duplikat-Kappung als Exclusion-Draw (garantiert terminierend, 1 RNG-Advance/Slot).
 */
import type { BalanceConfig } from '../config/balance'
import type { RunState, ShopSlot, Tier } from './types'
import { drawWeighted } from './rngStreams'

export interface OddsRow {
  common: number
  rare: number
  epic: number
}

/**
 * Stage-Odds fuer eine Runde (letzte Matrix-Zeile mit fromRound <= round gewinnt).
 * Gemeinsame Quelle fuer Shop-Slots, Bonus-Charakter UND Ghost-Seats (S3 v1.2 Punkt 5).
 */
export function stageOddsForRound(cfg: BalanceConfig, round: number): OddsRow {
  const matrix = cfg.rarity.stageOdds
  if (!matrix || matrix.length === 0) return cfg.rarity.odds
  let row: OddsRow = cfg.rarity.odds
  for (const r of matrix) {
    if (round >= r.fromRound) row = r
  }
  return row
}

/** Odds inkl. Seltenheits-Fokus (relativer Multiplikator auf die Stage-Odds) */
function effectiveOdds(state: RunState, kind: 'champion' | 'item'): OddsRow {
  const base = stageOddsForRound(state.config, state.round)
  const focused =
    (kind === 'champion' && state.focus === 'rarityChamps') ||
    (kind === 'item' && state.focus === 'rarityItems')
  if (!focused) return base
  const m = state.config.rarity.focusRarityMultPct
  return {
    common: Math.floor((base.common * m.common) / 100),
    rare: Math.floor((base.rare * m.rare) / 100),
    epic: Math.floor((base.epic * m.epic) / 100),
  }
}

export function championPrice(cfg: BalanceConfig, key: string): number {
  return cfg.rarity.prices[cfg.champions[key]!.rarity]
}

export function weaponPrice(cfg: BalanceConfig, key: string, tier: Tier): number {
  let price = cfg.rarity.prices[cfg.weapons[key]!.rarity]
  for (let t = 1; t < tier; t++) price = Math.floor((price * cfg.shop.tierPriceMultPct) / 100)
  return price
}

export function passivePrice(cfg: BalanceConfig, key: string): number {
  return cfg.rarity.prices[cfg.passives[key]!.rarity]
}

/**
 * Champion-Slot ziehen. `exclude`: Champion-Keys, die in diesem Shop-Board bereits
 * an der Duplikat-Kappe sind (S3b, Exclusion-Draw). Feasibility-Fallback: bleiben
 * nach Ausschluss keine Eintraege, wird die Kappe fuer diesen Slot relaxt.
 */
export function drawChampionSlot(state: RunState, opts: { exclude?: Set<string> } = {}): ShopSlot {
  const cfg = state.config
  const odds = effectiveOdds(state, 'champion')
  const owned = new Set(state.champions.map((c) => c.champ))
  const buildEntries = (exclude: Set<string> | undefined): Array<[string, number]> => {
    const out: Array<[string, number]> = []
    for (const k of Object.keys(cfg.champions)) {
      if (exclude?.has(k)) continue
      const champ = cfg.champions[k]!
      const rarityWeight = odds[champ.rarity]
      if (rarityWeight <= 0) continue // Gewicht 0 = ausgeschlossen (kein max(1,w)-Leck)
      let w = 100 * rarityWeight
      if (state.focus === 'merge' && owned.has(k)) w = Math.floor((w * cfg.focus.ownedWeightMultPct) / 100)
      if (state.focus === 'variety' && !owned.has(k)) w = Math.floor((w * cfg.focus.unownedWeightMultPct) / 100)
      if (w > 0) out.push([k, w])
    }
    return out
  }
  let entries = buildEntries(opts.exclude)
  if (entries.length === 0) entries = buildEntries(undefined) // Feasibility-Fallback: Kappe relaxen
  const key = drawWeighted(state.masterSeed, state.rngCounters, 'shop.champion', entries)
  return {
    kind: 'champion', key, tier: 1, rarity: cfg.champions[key]!.rarity,
    price: championPrice(cfg, key), locked: false, bought: false,
  }
}

/**
 * S1 v1.2: Bonus-Charakter — zieht FOKUS-NEUTRAL (Sicherheitsnetz, kein Fokus-
 * Verstaerker); v0 mit 5er-Pool auf Commons fixiert (Config bonusChampCommonOnly).
 */
export function drawBonusChampKey(state: RunState): string {
  const cfg = state.config
  const entries: Array<[string, number]> = []
  if (cfg.shop.bonusChampCommonOnly > 0) {
    for (const k of Object.keys(cfg.champions)) {
      if (cfg.champions[k]!.rarity === 'common') entries.push([k, 100])
    }
  }
  if (entries.length === 0) {
    const odds = stageOddsForRound(cfg, state.round) // ohne Fokus-Mod, ohne Merge/Variety
    for (const k of Object.keys(cfg.champions)) {
      const w = odds[cfg.champions[k]!.rarity]
      if (w > 0) entries.push([k, 100 * w])
    }
  }
  return drawWeighted(state.masterSeed, state.rngCounters, 'shop.bonusChamp', entries)
}

/** Waffen-Slot; hoehere Tiers erscheinen erst nach eigenem Craft (docs/02), mit kleinem Gewicht */
export function drawWeaponSlot(state: RunState): ShopSlot {
  const cfg = state.config
  const odds = effectiveOdds(state, 'item')
  const entries: Array<[string, number]> = []
  for (const k of Object.keys(cfg.weapons)) {
    if (k === 'fist') continue
    const w = odds[cfg.weapons[k]!.rarity]
    if (w > 0) entries.push([k, 100 * w])
  }
  const key = drawWeighted(state.masterSeed, state.rngCounters, 'shop.weapon', entries)
  // tier draw: base 100, unlocked tiers get tierOfferWeightPct each
  const maxCrafted = state.craftedWeaponTiers[key] ?? 1
  const tierEntries: Array<[string, number]> = [['1', 100]]
  for (let t = 2; t <= Math.min(maxCrafted, cfg.upgrade.weaponMaxTier); t++) {
    tierEntries.push([String(t), cfg.shop.tierOfferWeightPct])
  }
  const tier = Number(
    drawWeighted(state.masterSeed, state.rngCounters, 'shop.weapon.tier', tierEntries),
  ) as Tier
  return {
    kind: 'weapon', key, tier, rarity: cfg.weapons[key]!.rarity,
    price: weaponPrice(cfg, key, tier), locked: false, bought: false,
  }
}

export function drawPassiveSlot(state: RunState): ShopSlot {
  const cfg = state.config
  const odds = effectiveOdds(state, 'item')
  const entries: Array<[string, number]> = []
  for (const k of Object.keys(cfg.passives)) {
    const w = odds[cfg.passives[k]!.rarity]
    if (w > 0) entries.push([k, 100 * w])
  }
  const key = drawWeighted(state.masterSeed, state.rngCounters, 'shop.passive', entries)
  return {
    kind: 'passive', key, tier: 1, rarity: cfg.passives[key]!.rarity,
    price: passivePrice(cfg, key), locked: false, bought: false,
  }
}
